/**
 * استلام الطلبات — صفحة لكل يوم تنفيذ
 *
 * مدخل بيانات الفرع: يؤكّد ما وصله فعلاً لكل صنف (افتراضياً = المطلوب)،
 *   والملاحظة إلزامية عند أي فرق. بعد التأكيد يُقفل الطلب نهائياً.
 * البقية: يرون حالة استلام كل الطلبات لذلك اليوم؛ مدير النظام يستطيع إعادة الفتح.
 *
 * نافذة الاستلام (متى يجوز) محسوبة في الخادم (receiptWindow) — لا حساب هنا.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate, formatDateLong, formatDateTime, formatNumber, todayISO, addDays } from "../../lib/date";
import { ITEM_UNIT_LABELS } from "../../types";
import type { MealEntry } from "../../types";

const errMsg = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? fallback;

/** حالة استلام الطلب كنص ولون — مصدرها حقول الخادم فقط */
function receiptStatus(e: MealEntry): { label: string; cls: string } {
  if (e.receivedAt)           return { label: "مستلَم", cls: "bg-primary-50 text-primary-800" };
  if (e.receiptWindow.open)   return { label: "بانتظار الاستلام", cls: "bg-amber-50 text-amber-700" };
  if (e.entryDate > todayISO()) return { label: "لم يحن يوم التنفيذ", cls: "bg-gray-100 text-gray-600" };
  return { label: "لم يُستلم — انتهت المهلة", cls: "bg-red-50 text-red-700" };
}

/** جدول مقارنة المطلوب بالمستلم — للقراءة فقط */
function LinesComparison({ entry }: { entry: MealEntry }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-xs text-gray-500">
          <th className="text-right py-1 px-2">الصنف</th>
          <th className="text-center py-1 px-2">المطلوب</th>
          <th className="text-center py-1 px-2">المستلم</th>
          <th className="text-center py-1 px-2">الفرق</th>
        </tr>
      </thead>
      <tbody>
        {entry.lines.map((l) => {
          const rec = l.receivedQty;
          const diff = rec == null ? null : rec - l.qty;
          return (
            <tr key={l.id} className="border-b last:border-0">
              <td className="py-1 px-2">
                {l.item.nameAr} <span className="text-[11px] text-gray-400">({ITEM_UNIT_LABELS[l.item.unit]})</span>
              </td>
              <td className="text-center py-1 px-2" dir="ltr">{formatNumber(l.qty)}</td>
              <td className="text-center py-1 px-2" dir="ltr">{rec == null ? "—" : formatNumber(rec)}</td>
              <td className={`text-center py-1 px-2 font-semibold ${
                diff == null || diff === 0 ? "text-gray-400" : diff < 0 ? "text-red-700" : "text-amber-600"
              }`} dir="ltr">
                {diff == null ? "—" : diff === 0 ? "0" : `${diff > 0 ? "+" : ""}${formatNumber(diff)}`}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** نموذج تأكيد الاستلام — لمدخل بيانات الفرع */
function ReceiveForm({ entry, onDone }: { entry: MealEntry; onDone: (msg: string) => void }) {
  const [qty, setQty]         = useState<Record<number, string>>({});
  const [notes, setNotes]     = useState(entry.receiptNotes ?? "");
  const [confirming, setConfirming] = useState(false);
  const [error, setError]     = useState("");
  const [saving, setSaving]   = useState(false);

  // افتراضياً = المطلوب، أو ما سُجّل سابقاً إن أعاد المشرف فتح الاستلام
  useEffect(() => {
    const q: Record<number, string> = {};
    for (const l of entry.lines) q[l.itemId] = String(l.receivedQty ?? l.qty);
    setQty(q);
    setNotes(entry.receiptNotes ?? "");
    setConfirming(false); setError("");
  }, [entry.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const parsed = entry.lines.map((l) => ({ line: l, value: parseFloat(qty[l.itemId] ?? "") }));
  const invalid = parsed.find((p) => !Number.isFinite(p.value) || p.value < 0);
  const differs = parsed.some((p) => Number.isFinite(p.value) && p.value !== p.line.qty);
  const notesMissing = differs && !notes.trim();

  const submit = async () => {
    setError("");
    if (invalid) { setError(`أدخل كمية مستلمة صحيحة لصنف "${invalid.line.item.nameAr}"`); return; }
    const badDec = parsed.find((p) => {
      const f = 10 ** p.line.item.maxDecimalPlaces;
      return Math.round(p.value * f) / f !== p.value;
    });
    if (badDec) {
      setError(badDec.line.item.maxDecimalPlaces === 0
        ? `الصنف "${badDec.line.item.nameAr}" لا يقبل كسوراً عشرية`
        : `الصنف "${badDec.line.item.nameAr}" يقبل ${badDec.line.item.maxDecimalPlaces} منزلة عشرية على الأكثر`);
      return;
    }
    if (notesMissing) { setError("الكمية المستلمة تختلف عن المطلوبة — اكتب ملاحظة توضّح السبب"); return; }
    if (!confirming) { setConfirming(true); return; }

    setSaving(true);
    try {
      const res = await api.post(`/meal-entries/${entry.id}/receive`, {
        lines: parsed.map((p) => ({ itemId: p.line.itemId, receivedQty: p.value })),
        notes: notes.trim() || null,
      });
      onDone((res.data as { message?: string }).message ?? "تم تأكيد الاستلام");
    } catch (err) {
      setError(errMsg(err, "تعذّر تأكيد الاستلام"));
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {entry.receiptReopenedAt && (
        <div className="alert-info text-sm">أعاد مدير النظام فتح الاستلام لهذا الطلب — راجع الكميات وأكّدها من جديد.</div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-xs text-gray-500">
              <th className="text-right py-2 px-2">الصنف</th>
              <th className="text-center py-2 px-2">المطلوب</th>
              <th className="text-center py-2 px-2">المستلم فعلاً</th>
              <th className="text-center py-2 px-2">الفرق</th>
            </tr>
          </thead>
          <tbody>
            {parsed.map(({ line: l, value }) => {
              const diff = Number.isFinite(value) ? value - l.qty : null;
              return (
                <tr key={l.id} className="border-b last:border-0">
                  <td className="py-2 px-2">
                    {l.item.nameAr} <span className="text-[11px] text-gray-400">({ITEM_UNIT_LABELS[l.item.unit]})</span>
                  </td>
                  <td className="text-center py-2 px-2" dir="ltr">{formatNumber(l.qty)}</td>
                  <td className="text-center py-2 px-2">
                    <input type="number" inputMode="decimal" min="0" dir="ltr"
                      step={l.item.maxDecimalPlaces > 0 ? String(1 / 10 ** l.item.maxDecimalPlaces) : "1"}
                      className="input w-28 text-center mx-auto" disabled={saving}
                      aria-label={`الكمية المستلمة — ${l.item.nameAr}`}
                      value={qty[l.itemId] ?? ""}
                      onChange={(e) => { setQty((q) => ({ ...q, [l.itemId]: e.target.value })); setConfirming(false); }} />
                  </td>
                  <td className={`text-center py-2 px-2 font-semibold ${
                    diff == null || diff === 0 ? "text-gray-400" : diff < 0 ? "text-red-700" : "text-amber-600"
                  }`} dir="ltr">
                    {diff == null ? "—" : diff === 0 ? "0" : `${diff > 0 ? "+" : ""}${formatNumber(diff)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div>
        <label className="label" htmlFor="rnotes">
          ملاحظات الاستلام {differs && <span className="text-red-700">(إلزامية — يوجد فرق)</span>}
        </label>
        <textarea id="rnotes" rows={2} className="input resize-none" disabled={saving}
          value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder={differs ? "مثال: وصل 80 غداء بدل 100" : "اختيارية"} />
      </div>

      {error && <div className="alert-error">{error}</div>}
      {confirming && (
        <div className="alert-info text-sm">
          بعد التأكيد يُقفل الطلب نهائياً ولا يمكن تعديله. اضغط «تأكيد نهائي» للمتابعة.
        </div>
      )}

      <div className="flex justify-end gap-2">
        {confirming && (
          <button type="button" className="btn-secondary" onClick={() => setConfirming(false)} disabled={saving}>
            رجوع
          </button>
        )}
        <button type="button" className="btn-primary" onClick={() => void submit()} disabled={saving}>
          {saving ? "جاري الحفظ..." : confirming ? "تأكيد نهائي" : "تأكيد الاستلام"}
        </button>
      </div>
    </div>
  );
}

/** إعادة فتح الاستلام — مدير النظام، بسبب مكتوب */
function ReopenBox({ entry, onDone }: { entry: MealEntry; onDone: (msg: string) => void }) {
  const [open, setOpen]     = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError]   = useState("");
  const [saving, setSaving] = useState(false);

  const label = entry.receivedAt ? "إعادة فتح الاستلام" : "فتح مهلة الاستلام للفرع";
  if (!open) {
    return (
      <button type="button" className="btn-secondary text-xs" onClick={() => setOpen(true)}>
        {label}
      </button>
    );
  }
  const submit = async () => {
    setError("");
    if (reason.trim().length < 3) { setError("اكتب سبب إعادة الفتح"); return; }
    setSaving(true);
    try {
      const res = await api.post(`/meal-entries/${entry.id}/reopen-receipt`, { reason: reason.trim() });
      onDone((res.data as { message?: string }).message ?? "تمت إعادة الفتح");
    } catch (err) {
      setError(errMsg(err, "تعذّرت إعادة الفتح"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-2 border border-sand-300 rounded-lg p-3 bg-sand-50">
      <label className="label" htmlFor={`reopen-${entry.id}`}>{label} — السبب (يُحفظ في سجل التدقيق)</label>
      <input id={`reopen-${entry.id}`} className="input" value={reason} disabled={saving}
        onChange={(e) => setReason(e.target.value)} placeholder="مثال: خطأ في كمية الخبز حسب مراجعة الفرع" />
      {error && <p className="text-xs text-red-700">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button type="button" className="btn-secondary text-xs" onClick={() => setOpen(false)} disabled={saving}>إلغاء</button>
        <button type="button" className="btn-danger text-xs" onClick={() => void submit()} disabled={saving}>
          {saving ? "..." : "تأكيد"}
        </button>
      </div>
    </div>
  );
}

export default function ReceiptPage() {
  const { user, isRole } = useAuth();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const date = /^\d{4}-\d{2}-\d{2}$/.test(sp.get("date") ?? "") ? sp.get("date")! : todayISO();
  const setDate = (d: string) => setSp(d ? { date: d } : {}, { replace: true });

  const isReceiver = isRole("DATA_ENTRY") && user?.directorateId != null;
  const isAdmin    = isRole("SYSTEM_ADMIN");

  const [success, setSuccess] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);

  const { data: pending = [] } = useQuery({
    queryKey: ["pending-receipts"],
    enabled: isReceiver,
    queryFn: () => api.get<{ data: Array<{ id: number; entryDate: string }> }>("/meal-entries/pending-receipts")
                      .then((r) => r.data.data),
  });

  const { data: entries = [], isLoading, error } = useQuery({
    queryKey: ["meal-entries", "receipt", date],
    queryFn: () => api.get<{ data: MealEntry[] }>(`/meal-entries?from=${date}&to=${date}&pageSize=200`)
                      .then((r) => r.data.data),
  });

  const counts = useMemo(() => ({
    total:    entries.length,
    received: entries.filter((e) => e.receivedAt).length,
  }), [entries]);

  const refresh = async (msg: string) => {
    setSuccess(msg);
    setExpanded(null);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["meal-entries"] }),
      qc.invalidateQueries({ queryKey: ["pending-receipts"] }),
      qc.invalidateQueries({ queryKey: ["report"] }),
    ]);
  };

  useEffect(() => { setSuccess(""); }, [date]);

  const mine = isReceiver ? entries[0] ?? null : null;

  return (
    <div className={isReceiver ? "max-w-3xl mx-auto" : ""}>
      <div className="page-header">
        <h1 className="page-title">استلام الطلبات</h1>
        <p className="page-subtitle">
          {isReceiver
            ? "أكّد الكميات التي وصلت فعلاً لكل صنف — يوم التنفيذ أو خلال المهلة المحددة"
            : "حالة استلام الطلبات لكل فرع في يوم التنفيذ المحدد"}
        </p>
      </div>

      {isReceiver && pending.length > 0 && (
        <div className="alert-info mb-4 text-sm flex flex-wrap items-center gap-2">
          <span>بانتظار تأكيد الاستلام:</span>
          {pending.map((p) => (
            <button key={p.id} type="button" onClick={() => setDate(p.entryDate)}
              className={`px-2 py-0.5 rounded-full border text-xs ${
                p.entryDate === date ? "bg-primary-600 text-white border-primary-600" : "border-gold-700 hover:bg-white"
              }`}>
              {formatDate(p.entryDate)}
            </button>
          ))}
        </div>
      )}

      <div className="card mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label" htmlFor="rdate">يوم التنفيذ</label>
          <input id="rdate" type="date" className="input w-44" dir="ltr"
            value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary text-xs" onClick={() => setDate(addDays(date, -1))}>اليوم السابق</button>
          <button type="button" className="btn-secondary text-xs" onClick={() => setDate(todayISO())}>اليوم</button>
          <button type="button" className="btn-secondary text-xs" onClick={() => setDate(addDays(date, 1))}>اليوم التالي</button>
        </div>
        <p className="text-sm text-sand-600 mr-auto">{formatDateLong(date)}</p>
      </div>

      {success && <div className="alert-success mb-4">{success}</div>}
      {isLoading && <div className="card text-center text-gray-400 py-10">جاري التحميل...</div>}
      {error && <div className="alert-error">تعذّر تحميل الطلبات</div>}

      {/* ── مدخل بيانات الفرع: طلب مديريته لهذا اليوم ── */}
      {isReceiver && !isLoading && (
        !mine ? (
          <div className="card text-center text-gray-400 py-10">لا يوجد طلب لمديريتك في هذا اليوم</div>
        ) : (
          <div className="card space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-semibold">{mine.directorate.nameAr} — {mine.governorate.nameAr}</p>
                {mine.restaurant && <p className="text-xs text-sand-500">المطعم: {mine.restaurant.name}</p>}
              </div>
              <span className={`badge ${receiptStatus(mine).cls}`}>{receiptStatus(mine).label}</span>
            </div>

            {mine.receivedAt ? (
              <>
                <LinesComparison entry={mine} />
                <p className="text-xs text-sand-500">
                  أكّده {mine.receivedBy?.fullName ?? "—"} في <span dir="ltr">{formatDateTime(mine.receivedAt)}</span>
                  {mine.receiptNotes && <> — ملاحظة: {mine.receiptNotes}</>}
                </p>
              </>
            ) : mine.receiptWindow.open ? (
              <ReceiveForm entry={mine} onDone={(m) => void refresh(m)} />
            ) : (
              <>
                <div className="alert-error text-sm">{mine.receiptWindow.reason}</div>
                <LinesComparison entry={mine} />
              </>
            )}
          </div>
        )
      )}

      {/* ── البقية: كل طلبات اليوم وحالة استلامها ── */}
      {!isReceiver && !isLoading && (
        entries.length === 0 ? (
          <div className="card text-center text-gray-400 py-10">لا توجد طلبات لهذا اليوم</div>
        ) : (
          <>
            <p className="text-sm text-sand-600 mb-2">
              مستلَم <strong dir="ltr">{counts.received}</strong> من <strong dir="ltr">{counts.total}</strong> طلب
            </p>
            <div className="card overflow-x-auto p-0">
              <table className="w-full text-sm whitespace-nowrap">
                <thead>
                  <tr className="border-b-2 border-gray-200 text-xs">
                    <th className="text-right py-2 px-3">المحافظة</th>
                    <th className="text-right py-2 px-3">المديرية / الفرع</th>
                    <th className="text-right py-2 px-3">الحالة</th>
                    <th className="text-right py-2 px-3">أكّده</th>
                    <th className="text-center py-2 px-3">أصناف بفرق</th>
                    <th className="text-right py-2 px-3">ملاحظات الاستلام</th>
                    <th className="py-2 px-3" />
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const st = receiptStatus(e);
                    const diffCount = e.receivedAt
                      ? e.lines.filter((l) => l.receivedQty != null && l.receivedQty !== l.qty).length : null;
                    const isOpen = expanded === e.id;
                    return (
                      <React.Fragment key={e.id}>
                        <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded(isOpen ? null : e.id)}>
                          <td className="py-2 px-3">{e.governorate.nameAr}</td>
                          <td className="py-2 px-3 font-medium">{e.directorate.nameAr}</td>
                          <td className="py-2 px-3"><span className={`badge ${st.cls}`}>{st.label}</span></td>
                          <td className="py-2 px-3 text-xs text-gray-500">
                            {e.receivedAt ? <>{e.receivedBy?.fullName} <span dir="ltr">{formatDateTime(e.receivedAt)}</span></> : "—"}
                          </td>
                          <td className={`text-center py-2 px-3 ${diffCount ? "text-red-700 font-semibold" : "text-gray-400"}`} dir="ltr">
                            {diffCount == null ? "—" : diffCount}
                          </td>
                          <td className="py-2 px-3 text-gray-500 max-w-[14rem] truncate" title={e.receiptNotes ?? undefined}>
                            {e.receiptNotes || "—"}
                          </td>
                          <td className="py-2 px-3 text-xs text-primary-600">{isOpen ? "إخفاء ▲" : "تفاصيل ▼"}</td>
                        </tr>
                        {isOpen && (
                          <tr className="border-b bg-sand-50">
                            <td colSpan={7} className="p-3 whitespace-normal">
                              <div className="grid gap-3 md:grid-cols-[1fr_auto] items-start">
                                <LinesComparison entry={e} />
                                {isAdmin && (e.receivedAt || (!e.receiptWindow.open && e.entryDate <= todayISO())) && (
                                  <ReopenBox entry={e} onDone={(m) => void refresh(m)} />
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )
      )}
    </div>
  );
}
